import { SearchOutlined, ClearOutlined, ToolOutlined } from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import {
  Typography,
  Row,
  Col,
  Card,
  Table,
  Input,
  DatePicker,
  Button,
  Tag,
  Space,
  Statistic,
  Select,
  ConfigProvider
} from "antd";
import dayjs from "dayjs";
import { useState, useMemo, useEffect } from "react";

import Layout from "../components/common/Layout";

import styles from "./WhatsOnSitePage.module.css";

import type { ColumnsType } from "antd/es/table";

import { useUser } from "@/contexts/UserContext";
import { useProjects } from "@/hooks/businessCentral";
import {
  useGetAllInternalPlantHireLinesForProject,
  useGetAllExternalPlantHireLinesForProject,
  IInternalPlantHireLine,
  IExternalPlantHireLine
} from "@/hooks/plantItems";
import { ISelectOption } from "@/types";
import storage from "@/utils/storage";

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface IFilters {
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
  status?: string;
}

type PlantHireLine = IInternalPlantHireLine | IExternalPlantHireLine;

// BC returns "0001-01-01" for a blank date field.
const BC_EMPTY_DATE = "0001-01-01";

const toHireDate = (date?: string | null) =>
  date && date !== BC_EMPTY_DATE ? date : undefined;

// Internal lines carry onHireDate/offHireDate; external lines carry the EQ2 variants.
const getHireDates = (line: PlantHireLine) =>
  "onHireDateEQ2" in line
    ? {
        onHire: toHireDate(line.onHireDateEQ2),
        offHire: toHireDate(line.offHireDateEQ2)
      }
    : {
        onHire: toHireDate(line.onHireDate),
        offHire: toHireDate(line.offHireDate)
      };

// On hire = has an on-hire date, and either no off-hire date yet or one that is today or later.
const isOnHire = (line: PlantHireLine) => {
  const { onHire, offHire } = getHireDates(line);
  return !!onHire && (!offHire || !dayjs(offHire).isBefore(dayjs(), "day"));
};

// Booked but not yet on site: the on-hire date is after today.
const isUpcoming = (line: PlantHireLine) => {
  const { onHire } = getHireDates(line);
  return !!onHire && dayjs(onHire).isAfter(dayjs(), "day");
};

const DocumentNoCell = ({ line }: { line: PlantHireLine }) => (
  <Space size={8}>
    {line.documentNo}
    {isUpcoming(line) && (
      <span className="text-xs font-semibold text-amber-700">Upcoming</span>
    )}
  </Space>
);

// Roomier rows than antd's default 16px vertical cell padding.
const TABLE_THEME = { components: { Table: { cellPaddingBlock: 20 } } };

const getRowClassName = (line: PlantHireLine) =>
  isUpcoming(line) ? styles.upcomingRow : "";

const STATUS_OPTIONS = [
  { label: "Pending Approval", value: "pending approval" },
  { label: "Released", value: "released" }
];

// BC encodes spaces as "_x0020_", e.g. "Pending_x0020_Approval" -> "pending approval".
const normalizeStatus = (status?: string | null) =>
  (status ?? "").replace(/_x0020_/g, " ").toLowerCase();

const LISTED_STATUSES = new Set(STATUS_OPTIONS.map(({ value }) => value));

const hasListedStatus = (line: PlantHireLine) =>
  LISTED_STATUSES.has(normalizeStatus(line.status));

// Shown at all = on hire, with a status the Status filter offers.
const isShown = (line: PlantHireLine) =>
  isOnHire(line) && hasListedStatus(line);

const compareText = (a?: string | null, b?: string | null) =>
  (a ?? "").localeCompare(b ?? "");

const formatDate = (date?: string | null) =>
  date ? dayjs(date).format("DD MMM, YYYY") : "-";

// Search covers only the fields users identify equipment by: Document No, Plant ID and Equipment Name.
const matchesSearch = (item: PlantHireLine, term: string) =>
  [item.documentNo, item.no, item.description].some((field) =>
    field?.toLowerCase().includes(term)
  );

const filterPlantHireLines = <T extends PlantHireLine>(
  items: T[],
  { searchTerm, fromDate, toDate, status }: IFilters
): T[] => {
  const term = searchTerm?.trim().toLowerCase();

  return items.filter((item) => {
    // Slice to YYYY-MM-DD so a timestamped value still compares by day.
    const onHireDate = getHireDates(item).onHire?.slice(0, 10) ?? "";
    return (
      (!term || matchesSearch(item, term)) &&
      (!fromDate || onHireDate >= fromDate) &&
      (!toDate || onHireDate <= toDate) &&
      (!status || normalizeStatus(item.status) === status)
    );
  });
};

const PLANT_HIRE_COLUMNS: ColumnsType<PlantHireLine> = [
  {
    title: "Document No",
    dataIndex: "documentNo",
    width: 220,
    ellipsis: true,
    fixed: "left",
    sorter: (a, b) => compareText(a.documentNo, b.documentNo),
    render: (_, line) => <DocumentNoCell line={line} />
  },
  {
    title: "Project Task No",
    dataIndex: "projectTaskNo",
    width: 160,
    ellipsis: true,
    sorter: (a, b) => compareText(a.projectTaskNo, b.projectTaskNo)
  },
  {
    title: "Description",
    dataIndex: "projectTaskDescription",
    width: 200,
    ellipsis: true,
    sorter: (a, b) =>
      compareText(a.projectTaskDescription, b.projectTaskDescription),
    render: (text) => text || "-"
  },
  {
    title: "Plant ID",
    dataIndex: "no",
    width: 120,
    ellipsis: true,
    sorter: (a, b) => compareText(a.no, b.no),
    render: (text) => <Text strong>{text}</Text>
  },
  {
    title: "Equipment Name",
    dataIndex: "description",
    width: 220,
    ellipsis: true,
    sorter: (a, b) => compareText(a.description, b.description)
  },
  {
    title: "Site Location",
    dataIndex: "projectName",
    width: 200,
    ellipsis: true,
    sorter: (a, b) => compareText(a.projectName, b.projectName)
  },
  {
    title: "On Hire Date",
    key: "onHireDate",
    width: 140,
    ellipsis: true,
    sorter: (a, b) =>
      compareText(getHireDates(a).onHire, getHireDates(b).onHire),
    render: (_, line) => formatDate(getHireDates(line).onHire)
  },
  {
    title: "Off Hire Date",
    key: "offHireDate",
    width: 140,
    ellipsis: true,
    sorter: (a, b) =>
      compareText(getHireDates(a).offHire, getHireDates(b).offHire),
    render: (_, line) => formatDate(getHireDates(line).offHire)
  }
];

const WhatsOnSitePage = () => {
  const [selectedProject, setSelectedProject] = useState<ISelectOption | null>(
    null
  );
  const [filters, setFilters] = useState<IFilters>({});

  const user = useUser();

  const { isLoading: projectsLoading, data: projectsData } = useProjects();
  const { isLoading: internalPlantsLoading, data: internalPlantsData } =
    useGetAllInternalPlantHireLinesForProject(selectedProject?.value);
  const { isLoading: externalPlantsLoading, data: externalPlantsData } =
    useGetAllExternalPlantHireLinesForProject(selectedProject?.value);

  const shownInternal = useMemo(
    () => internalPlantsData?.value?.filter(isShown) ?? [],
    [internalPlantsData]
  );
  const shownExternal = useMemo(
    () => externalPlantsData?.value?.filter(isShown) ?? [],
    [externalPlantsData]
  );

  useEffect(() => {
    if (projectsData && projectsData.value?.length > 0) {
      const savedProjectNo = storage.getProject()?.split("~~")?.[0];

      if (savedProjectNo) {
        const target = projectsData.value.find(
          (item) =>
            user?.regionCode === item.jobPostingGroup &&
            item.no === savedProjectNo
        );
        if (target) {
          setSelectedProject({
            key: target.no,
            value: target.no,
            label: target.no + " - " + target.description,
            no: target.no,
            description: target.description
          });
        }
      }
    }
  }, [user, projectsData]);

  const projectOptions = useMemo(() => {
    const options: ISelectOption[] = [];
    if (user?.regionCode && projectsData && projectsData?.value?.length > 0) {
      projectsData.value.forEach((project) => {
        if (
          project.jobPostingGroup &&
          project.jobPostingGroup === user.regionCode
        ) {
          options.push({
            value: project.no,
            label: project.no + " - " + project.description
          });
        }
      });
    }
    return options;
  }, [user, projectsData]);

  const filteredInternal = useMemo(
    () => filterPlantHireLines(shownInternal, filters),
    [shownInternal, filters]
  );
  const filteredExternal = useMemo(
    () => filterPlantHireLines(shownExternal, filters),
    [shownExternal, filters]
  );

  const updateFilters = (newFilters: Partial<IFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      updateFilters({
        fromDate: dates[0].format("YYYY-MM-DD"),
        toDate: dates[1].format("YYYY-MM-DD")
      });
    } else {
      updateFilters({ fromDate: undefined, toDate: undefined });
    }
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: ""
    });
  };

  const hasActiveFilters =
    filters.fromDate || filters.toDate || filters.searchTerm || filters.status;

  return (
    <Layout>
      <div className="table_above_section">
        <div className="mb-4">
          <PageHeader
            title="What's on Site"
            description="Real-time equipment tracking from E-Vision Plant Module"
          />
        </div>

        {/* Project Selection */}
        <div className="mt-4 p-4 bg-white rounded-lg">
          <Select
            showSearch
            size="large"
            options={projectOptions}
            placeholder={
              projectsLoading ? "Loading projects..." : "Search by project name"
            }
            value={selectedProject}
            onSelect={(_, obj) => {
              setSelectedProject(obj);
            }}
            disabled={projectsLoading}
            optionFilterProp="label"
            style={{ width: "100%", maxWidth: 400 }}
          />
        </div>

        {/* Summary Statistics */}
        <Row gutter={16} className="my-4">
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Equipment"
                value={shownInternal.length + shownExternal.length}
                prefix={<ToolOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Internal (Company Owned)"
                value={shownInternal.length}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="External (Rented)"
                value={shownExternal.length}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="mb-4">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={6}>
              <Input
                size="large"
                placeholder="Search by Doc No, Plant ID or Name..."
                prefix={<SearchOutlined />}
                value={filters?.searchTerm}
                onChange={(e) =>
                  updateFilters({
                    searchTerm: e.target.value
                  })
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                size="large"
                style={{ width: "100%" }}
                placeholder={["On Hire From", "On Hire To"]}
                onChange={handleDateRangeChange}
                value={
                  filters.fromDate && filters.toDate
                    ? [dayjs(filters.fromDate), dayjs(filters.toDate)]
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                size="large"
                placeholder="Status"
                style={{ width: "100%" }}
                allowClear
                value={filters.status}
                onChange={(value) => updateFilters({ status: value })}
                options={STATUS_OPTIONS}
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              {hasActiveFilters && (
                <Button
                  size="large"
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              )}
            </Col>
          </Row>
        </Card>
      </div>

      {/* Split View Tables */}
      <ConfigProvider theme={TABLE_THEME}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <span style={{ color: "#1890ff", fontWeight: 600 }}>
                    Internal Equipment
                  </span>
                  <Tag color="blue">{filteredInternal.length}</Tag>
                </Space>
              }
              styles={{ body: { padding: 0 } }}
            >
              <Table
                columns={PLANT_HIRE_COLUMNS}
                dataSource={filteredInternal}
                rowKey="@odata.etag"
                rowClassName={getRowClassName}
                loading={internalPlantsLoading}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                }}
                scroll={{ x: "max-content", y: 700 }}
              />
            </Card>
          </Col>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <span style={{ color: "#722ed1", fontWeight: 600 }}>
                    External Equipment (Rented)
                  </span>
                  <Tag color="purple">{filteredExternal.length}</Tag>
                </Space>
              }
              styles={{ body: { padding: 0 } }}
            >
              <Table
                columns={PLANT_HIRE_COLUMNS}
                dataSource={filteredExternal}
                rowKey="@odata.etag"
                rowClassName={getRowClassName}
                loading={externalPlantsLoading}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                }}
                scroll={{ x: "max-content", y: 700 }}
              />
            </Card>
          </Col>
        </Row>
      </ConfigProvider>
    </Layout>
  );
};

export default WhatsOnSitePage;
